import {
    FormGrid, FormSection, FieldInput, FieldSelect, FieldTextarea,
} from "../../_CORE/APP/Templates/_UI/Form/_index";

export const DATABASE_CREDENTIAL_TYPES = new Set(["DATABASE", "DATABASE_ADMIN"]);
export const GIT_CREDENTIAL_TYPES = new Set(["REPOSITORY", "CI_CD"]);

const GENERIC_FIELDS_BY_TYPE = {
    DATABASE: ["host", "port", "databaseName", "connectionString", "token"],
    DATABASE_ADMIN: ["host", "port", "databaseName", "connectionString", "token"],
    SERVER: ["host", "port", "token"],
    VPS: ["host", "port", "token"],
    HOSTING: ["host", "port", "token"],
    SSH: ["host", "port", "token"],
    RDP: ["host", "port"],
    VPN: ["host", "port", "token"],
    FTP: ["host", "port"],
    SFTP: ["host", "port"],
    API_KEY: ["host", "token"],
    TOKEN: ["host", "token"],
    OAUTH: ["host", "token"],
    SSO: ["host", "token"],
    EMAIL: ["host", "port"],
    SMTP: ["host", "port"],
    IMAP: ["host", "port"],
    PANEL: ["host", "token"],
    REPOSITORY: ["host", "token"],
    CI_CD: ["host", "token"],
    CONTAINER_REGISTRY: ["host", "token"],
    KUBERNETES: ["host", "token"],
    REDIS: ["host", "port", "connectionString"],
    QUEUE: ["host", "port", "connectionString"],
    CLOUD: ["host", "token", "connectionString"],
    STORAGE: ["host", "token", "connectionString"],
    SECRET_MANAGER: ["host", "token"],
    MONITORING: ["host", "token"],
    ANALYTICS: ["host", "token"],
    DOMAIN: ["host", "token"],
    DNS: ["host", "token"],
    CDN: ["host", "token"],
    WEBHOOK: ["host", "token"],
    CERTIFICATE: ["host", "token"],
    PAYMENT: ["token"],
    LICENSE: ["token"],
    ACCOUNT: ["token"],
    BANK: ["token"],
    SOCIAL: ["token"],
    SUBSCRIPTION: ["token"],
    STREAMING: ["token"],
    SHOPPING: ["token"],
    EDUCATION: ["token"],
    PROFESSIONAL: ["token"],
    GAMING: ["token"],
    TRAVEL: ["token"],
    GOVERNMENT: ["token"],
    INSURANCE: ["token"],
    WIFI: ["host", "token"],
    DEVICE: ["host", "token"],
    IDENTITY: [],
    OTHER: ["host", "port", "token", "databaseName", "connectionString"],
};

const REMOTE_CREDENTIAL_TYPES = new Set([
    "SERVER", "VPS", "HOSTING", "SSH", "RDP", "VPN", "FTP", "SFTP",
    "KUBERNETES", "CONTAINER_REGISTRY", "OTHER",
]);

export const onCredentialGenericFields = (type) => (
    GENERIC_FIELDS_BY_TYPE[String(type || "OTHER").toUpperCase()] ?? GENERIC_FIELDS_BY_TYPE.OTHER
);

const TECHNICAL_DEFINITIONS = {
    databaseEngine: { label: "Motor / proveedor de base de datos", isSecret: false },
    databaseSchema: { label: "Esquema", isSecret: false },
    databaseInstance: { label: "Instancia / servicio", isSecret: false },
    databaseSslMode: { label: "Modo SSL / TLS", isSecret: false },
    databaseCharset: { label: "Charset / codificación", isSecret: false },
    databaseOptions: { label: "Parámetros de conexión", isSecret: true },

    gitProvider: { label: "Plataforma Git", isSecret: false },
    gitOrganization: { label: "Organización / grupo", isSecret: false },
    gitRepository: { label: "Repositorio", isSecret: false },
    gitDefaultBranch: { label: "Rama por defecto", isSecret: false },
    gitHttpsUrl: { label: "Clone HTTPS", isSecret: false },
    gitSshUrl: { label: "Clone SSH", isSecret: false },
    gitProjectId: { label: "ID del proyecto / repositorio", isSecret: false },
    gitTokenScopes: { label: "Scopes / permisos del token", isSecret: false },
    gitTokenExpiresAt: { label: "Vencimiento del token", isSecret: false },
};

const KEY_BY_LABEL = Object.fromEntries(
    Object.entries(TECHNICAL_DEFINITIONS).map(([key, definition]) => [definition.label, key]),
);

export const DATABASE_ENGINE_OPTIONS = [
    { value: "", label: "Sin especificar" },
    { value: "PostgreSQL", label: "PostgreSQL" },
    { value: "MySQL", label: "MySQL" },
    { value: "MariaDB", label: "MariaDB" },
    { value: "Microsoft SQL Server", label: "Microsoft SQL Server" },
    { value: "Oracle", label: "Oracle" },
    { value: "MongoDB", label: "MongoDB" },
    { value: "SQLite", label: "SQLite" },
    { value: "CockroachDB", label: "CockroachDB" },
    { value: "Redis", label: "Redis" },
    { value: "Otro", label: "Otro / personalizado" },
];

export const DATABASE_SSL_OPTIONS = [
    { value: "", label: "Sin especificar" },
    { value: "disable", label: "disable" },
    { value: "allow", label: "allow" },
    { value: "prefer", label: "prefer" },
    { value: "require", label: "require" },
    { value: "verify-ca", label: "verify-ca" },
    { value: "verify-full", label: "verify-full" },
];

export const GIT_PROVIDER_OPTIONS = [
    { value: "", label: "Sin especificar" },
    { value: "GitHub", label: "GitHub" },
    { value: "GitLab", label: "GitLab" },
    { value: "Bitbucket", label: "Bitbucket" },
    { value: "Azure DevOps", label: "Azure DevOps" },
    { value: "Gitea", label: "Gitea" },
    { value: "Codeberg", label: "Codeberg" },
    { value: "Otro", label: "Otro / self-hosted" },
];

export const onEmptyCredentialTechnical = () => Object.fromEntries(
    Object.keys(TECHNICAL_DEFINITIONS).map((key) => [key, ""]),
);

export const onIsDatabaseCredentialType = (type) => DATABASE_CREDENTIAL_TYPES.has(String(type || "").toUpperCase());
export const onIsGitCredentialType = (type) => GIT_CREDENTIAL_TYPES.has(String(type || "").toUpperCase());

export const onSplitCredentialTechnicalExtra = (extra = []) => {
    const technical = onEmptyCredentialTechnical();
    const custom = [];

    (Array.isArray(extra) ? extra : []).forEach((field) => {
        const key = KEY_BY_LABEL[String(field?.label ?? "").trim()];
        if (!key) {
            custom.push({ ...field });
            return;
        }
        technical[key] = field?.value ?? "";
    });

    return { technical, custom };
};

export const onMergeCredentialTechnicalExtra = (custom = [], technical = {}) => {
    const systemRows = Object.entries(TECHNICAL_DEFINITIONS)
        .map(([key, definition]) => ({
            label: definition.label,
            value: String(technical?.[key] ?? "").trim(),
            isSecret: !!definition.isSecret,
        }))
        .filter((field) => field.value !== "");

    const cleanCustom = (Array.isArray(custom) ? custom : []).filter((field) => {
        const label = String(field?.label ?? "").trim();
        return label !== "" && !KEY_BY_LABEL[label];
    });

    return [...systemRows, ...cleanCustom];
};

const onFieldVisible = (visibleFields, field) => !Array.isArray(visibleFields) || visibleFields.includes(field);

export const CredentialSpecializedSections = ({
    idPrefix = "credential",
    type,
    form,
    technical,
    onTechnicalChange,
    secretPlaceholder = "",
    secretHints = {},
    includeUrl = false,
    visibleFields = null,
    renderSshKey = true,
}) => {
    const IS_DATABASE = onIsDatabaseCredentialType(type);
    const IS_GIT = onIsGitCredentialType(type);
    const EFFECTIVE_VISIBLE_FIELDS = Array.isArray(visibleFields) ? visibleFields : onCredentialGenericFields(type);
    const SHOW_REMOTE_KEY = renderSshKey && REMOTE_CREDENTIAL_TYPES.has(String(type || "").toUpperCase());

    const techField = (key) => ({
        value: technical?.[key] ?? "",
        onChange: (event) => onTechnicalChange?.(key, event.target.value),
    });

    if (IS_DATABASE) {
        return (
            <FormSection
                title="Base de datos"
                description="Todos los datos de conexión quedan juntos: motor, servidor, base, usuario, contraseña, cadena y opciones técnicas."
            >
                <FormGrid columns={3}>
                    <FieldSelect id={`${idPrefix}_db_engine`} label="Motor / proveedor"
                        {...techField("databaseEngine")} options={DATABASE_ENGINE_OPTIONS} isSearchable />
                    <FieldInput {...form.field("host")} label="Host / endpoint" placeholder="Ej: db.example.com o 10.0.0.15" />
                    <FieldInput {...form.field("port")} label="Puerto" placeholder="Ej: 5432" />

                    <FieldInput {...form.field("databaseName")} label="Base de datos" placeholder="Ej: app_production" />
                    <FieldInput id={`${idPrefix}_db_schema`} label="Esquema" placeholder="Ej: public"
                        {...techField("databaseSchema")} />
                    <FieldInput id={`${idPrefix}_db_instance`} label="Instancia / servicio" placeholder="Ej: SQLEXPRESS, ORCLPDB1"
                        {...techField("databaseInstance")} />

                    <FieldInput {...form.field("username")} label="Usuario de base de datos" placeholder="Ej: app_user" />
                    <FieldInput {...form.field("secret")} type="pin" label="Contraseña de base de datos"
                        placeholder={secretPlaceholder} hint={secretHints.secret} />
                    <FieldInput {...form.field("passwordHint")} label="Pista / palabra secreta"
                        placeholder="Ej: palabra clave + año + símbolo"
                        hint={secretHints.passwordHint || "Guardá sólo un indicio, nunca la contraseña completa. Se guarda cifrada, pero NOTEBOOK te la muestra completa para que puedas verificarla y también la incluye por defecto al exportar para compartir."} />
                    <FieldInput {...form.field("email")} label="Correo asociado" placeholder="Ej: db-admin@example.com" />

                    <FieldInput {...form.field("connectionString")} type="pin" label="Cadena de conexión"
                        placeholder={secretPlaceholder || "postgresql://usuario:clave@host:5432/base"}
                        hint={secretHints.connectionString} span={2} />
                    {onFieldVisible(EFFECTIVE_VISIBLE_FIELDS, "token") && (
                        <FieldInput {...form.field("token")} type="pin" label="Token / API key del proveedor"
                            placeholder={secretPlaceholder} hint={secretHints.token} />
                    )}

                    <FieldSelect id={`${idPrefix}_db_ssl`} label="Modo SSL / TLS"
                        {...techField("databaseSslMode")} options={DATABASE_SSL_OPTIONS} />
                    <FieldInput id={`${idPrefix}_db_charset`} label="Charset / codificación" placeholder="Ej: UTF8 / utf8mb4"
                        {...techField("databaseCharset")} />
                    {includeUrl && (
                        <FieldInput {...form.field("url")} label="Panel / URL del proveedor" placeholder="Ej: https://console.example.com" />
                    )}

                    <FieldInput id={`${idPrefix}_db_options`} type="pin" label="Parámetros de conexión"
                        placeholder={secretPlaceholder || "Ej: application_name=api&connect_timeout=10"}
                        hint="Se guarda cifrado porque puede contener parámetros sensibles." {...techField("databaseOptions")} span={3} />

                    {renderSshKey && (
                        <FieldTextarea {...form.field("sshKey")} label="Clave SSH / túnel (opcional)" rows={4}
                            placeholder={secretPlaceholder || "Clave privada, deploy key o dato SSH usado para el túnel"} span={3} />
                    )}
                </FormGrid>
            </FormSection>
        );
    }

    if (IS_GIT) {
        return (
            <FormSection
                title="Git / repositorio"
                description="GitHub, GitLab, Bitbucket o Git self-hosted: cuenta, repositorio, clone URLs, rama y credenciales en un solo lugar."
            >
                <FormGrid columns={3}>
                    <FieldSelect id={`${idPrefix}_git_provider`} label="Plataforma"
                        {...techField("gitProvider")} options={GIT_PROVIDER_OPTIONS} isSearchable />
                    <FieldInput {...form.field("host")} label="Host / dominio Git" placeholder="Ej: github.com o gitlab.empresa.local" />
                    {includeUrl ? (
                        <FieldInput {...form.field("url")} label="Panel / URL del servicio" placeholder="Ej: https://gitlab.empresa.local" />
                    ) : (
                        <FieldInput id={`${idPrefix}_git_project_id`} label="ID del proyecto / repositorio" placeholder="Ej: 1842"
                            {...techField("gitProjectId")} />
                    )}

                    <FieldInput id={`${idPrefix}_git_org`} label="Organización / grupo" placeholder="Ej: equipo-backend"
                        {...techField("gitOrganization")} />
                    <FieldInput id={`${idPrefix}_git_repo`} label="Repositorio" placeholder="Ej: notebook-api"
                        {...techField("gitRepository")} />
                    <FieldInput id={`${idPrefix}_git_branch`} label="Rama por defecto" placeholder="Ej: main"
                        {...techField("gitDefaultBranch")} />

                    <FieldInput {...form.field("username")} label="Usuario / cuenta" placeholder="Ej: dev.user" />
                    <FieldInput {...form.field("email")} label="Correo asociado" placeholder="Ej: dev@example.com" />
                    <FieldInput {...form.field("secret")} type="pin" label="Contraseña / app password"
                        placeholder={secretPlaceholder} hint={secretHints.secret} />
                    <FieldInput {...form.field("passwordHint")} label="Pista / palabra secreta"
                        placeholder="Ej: palabra clave + año + símbolo"
                        hint={secretHints.passwordHint || "Guardá sólo un indicio, nunca la contraseña completa. Se guarda cifrada, pero NOTEBOOK te la muestra completa para que puedas verificarla y también la incluye por defecto al exportar para compartir."} />

                    <FieldInput {...form.field("token")} type="pin" label="Token / PAT"
                        placeholder={secretPlaceholder} hint={secretHints.token} />
                    <FieldInput id={`${idPrefix}_git_scopes`} label="Scopes / permisos del token" placeholder="Ej: repo, read_api, write_repository"
                        {...techField("gitTokenScopes")} />
                    <FieldInput id={`${idPrefix}_git_token_expiry`} type="date" label="Vencimiento del token"
                        {...techField("gitTokenExpiresAt")} />

                    <FieldInput id={`${idPrefix}_git_https`} label="Clone HTTPS" placeholder="https://github.com/organizacion/repositorio.git"
                        {...techField("gitHttpsUrl")} span={2} />
                    {includeUrl && (
                        <FieldInput id={`${idPrefix}_git_project_id_2`} label="ID del proyecto / repositorio" placeholder="Ej: 1842"
                            {...techField("gitProjectId")} />
                    )}

                    <FieldInput id={`${idPrefix}_git_ssh`} label="Clone SSH" placeholder="git@github.com:organizacion/repositorio.git"
                        {...techField("gitSshUrl")} span={3} />

                    {renderSshKey && (
                        <FieldTextarea {...form.field("sshKey")} label="Clave SSH / deploy key" rows={4}
                            placeholder={secretPlaceholder || "Clave privada, deploy key o dato SSH sensible"} span={3} />
                    )}
                </FormGrid>
            </FormSection>
        );
    }

    return (
        <FormSection title="Acceso y conexión" description="Datos generales de autenticación y conexión para el tipo seleccionado.">
            <FormGrid columns={3}>
                <FieldInput {...form.field("email")} label="Correo" placeholder="Ej: cuenta@example.com" />
                <FieldInput {...form.field("username")} label="Usuario" placeholder="Ej: admin_user" />
                <FieldInput {...form.field("secret")} type="pin" label="Contraseña"
                    placeholder={secretPlaceholder} hint={secretHints.secret} />
                <FieldInput {...form.field("passwordHint")} label="Pista / palabra secreta"
                    placeholder="Ej: palabra clave + año + símbolo"
                    hint={secretHints.passwordHint || "Guardá sólo un indicio, nunca la contraseña completa. Se guarda cifrada, pero NOTEBOOK te la muestra completa para que puedas verificarla y también la incluye por defecto al exportar para compartir."} />

                {onFieldVisible(EFFECTIVE_VISIBLE_FIELDS, "host") && <FieldInput {...form.field("host")} label="Host o dirección" placeholder="Ej: 192.168.100.10" />}
                {onFieldVisible(EFFECTIVE_VISIBLE_FIELDS, "port") && <FieldInput {...form.field("port")} label="Puerto" placeholder="Ej: 443" />}
                {onFieldVisible(EFFECTIVE_VISIBLE_FIELDS, "databaseName") && <FieldInput {...form.field("databaseName")} label="Base de datos" placeholder="Ej: app_production" />}
                {onFieldVisible(EFFECTIVE_VISIBLE_FIELDS, "token") && <FieldInput {...form.field("token")} type="pin" label="Token / API key / secreto"
                    placeholder={secretPlaceholder} hint={secretHints.token} />}
                {includeUrl && <FieldInput {...form.field("url")} label="Panel o dirección" placeholder="Ej: https://app.example.com" />}
                {onFieldVisible(EFFECTIVE_VISIBLE_FIELDS, "connectionString") && <FieldInput {...form.field("connectionString")} type="pin" label="Cadena de conexión"
                    placeholder={secretPlaceholder} hint={secretHints.connectionString} span={includeUrl ? 1 : 2} />}
                {SHOW_REMOTE_KEY && (
                    <FieldTextarea {...form.field("sshKey")} label="Clave privada / SSH" rows={4}
                        placeholder={secretPlaceholder || "Contenido de la clave privada o dato SSH sensible"} span={3} />
                )}
            </FormGrid>
        </FormSection>
    );
};
